import React from 'react';
import { Badge, BadgeSize } from '@usepepper/ui/atoms/badge';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useBadgeOverflow } from '@/components/orders/use-badge-overflow';
import { VariantPackInfoContext_GroupBadgeFieldsFragment } from '@/types';
import { View } from 'react-native';

export { sortBadgesByPriority } from '@/components/orders/use-badge-overflow';

export const MAX_VISIBLE_BADGES_CARD = 2;
export const MAX_VISIBLE_BADGES_MOBILE = 5;
export const MAX_VISIBLE_BADGES_DESKTOP = 8;

interface Props {
  badges?: VariantPackInfoContext_GroupBadgeFieldsFragment[];
  footer?: React.ReactNode;
  iconOnly?: boolean;
  maxVisible?: number;
}

export const BadgeLabelList: React.FC<Props> = ({
  badges,
  footer,
  iconOnly,
  maxVisible,
}) => {
  const [styles] = useStyles();
  const theme = useTheme();

  const { overflowCount, sortedBadges, visibleBadges } = useBadgeOverflow(
    badges,
    maxVisible,
  );

  if (!sortedBadges.length) {
    if (footer) {
      return <View style={styles.base}>{footer}</View>;
    }
    return null;
  }

  return (
    <View style={styles.base}>
      {visibleBadges.map((badge) =>
        iconOnly ? (
          <View
            accessibilityLabel={`${badge.name} badge`}
            key={badge.uuid}
            style={[
              styles.iconOnlyContainer,
              badge.background_color
                ? { backgroundColor: badge.background_color }
                : null,
            ]}
          >
            <CachedImage
              alt={`${badge.name} badge`}
              contentFit="contain"
              source={{ uri: badge.image_url }}
              style={styles.iconOnlyImage}
            />
            {badge.short_name ? (
              <Typography
                color={theme.sk.color.text.primary}
                variant="paragraph-sm"
              >
                {badge.short_name}
              </Typography>
            ) : null}
          </View>
        ) : (
          <Badge
            key={badge.uuid}
            label={badge.name}
            leftElement={
              <CachedImage
                alt=""
                contentFit="contain"
                source={{ uri: badge.image_url }}
                style={styles.badgeImage}
              />
            }
            size={BadgeSize.SMALL}
            style={[
              styles.container,
              badge.background_color
                ? { backgroundColor: badge.background_color }
                : null,
            ]}
            textStyle={styles.text}
          />
        ),
      )}
      {overflowCount > 0 ? (
        <View style={styles.overflowContainer}>
          <Typography variant="paragraph-sm">+{overflowCount}</Typography>
        </View>
      ) : null}
      {footer ? footer : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  badgeImage: {
    height: theme.sk.spacing.spaceMedium,
    width: theme.sk.spacing.spaceMedium,
  },
  base: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.sk.spacing.spaceSmall,
    marginTop: theme.sk.spacing.spaceSmall,
  },
  container: {
    backgroundColor: theme.sk.color.bg.highlight.secondary,
  },
  iconOnlyContainer: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.highlight.secondary,
    borderRadius: theme.sk.radii.radiusRound,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    justifyContent: 'flex-start',
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceXsmall,
  },
  iconOnlyImage: {
    borderRadius: theme.sk.radii.radiusSmall,
    height: theme.sk.spacing.spaceMedium,
    width: theme.sk.spacing.spaceMedium,
  },
  overflowContainer: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.highlight.secondary,
    borderRadius: theme.sk.radii.radiusRound,
    justifyContent: 'center',
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceXsmall,
  },
  text: {
    color: theme.sk.color.text.primary,
    fontSize: 12,
    fontWeight: '600',
    lineHeight: 13,
  },
}));
